import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDialog } from './CalendarDialog';
import { makeReservation, clearError } from '../redux/slices/myselfSlice';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingModal } from './LoadingModal';
import { getEquipmentTranslation } from '../utils/equipmentTranslation';

const CreateReservationModal = ({
  setShowCreateReservationModal,
  renterName,
  equipment,
  quantity,
  userId,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.myself.loading);
  const myselfError = useSelector((state) => state.myself.error);
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const equipmentName = getEquipmentTranslation(equipment, 'name', currentLang);
  const [formData, setFormData] = useState({
    deliveryAssistance: '',
    pickupAssistance: '',
    startDate: '',
    endDate: '',
    paymentMethod: '',
    additionalNotes: '',
    pickupLocation: '',
    dropOffLocation: '',
  });
  const [showTransportConfirmation, setShowTransportConfirmation] = useState(false);
  const [transportConfirmationChecked, setTransportConfirmationChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // 1 for date selection, 2 for details, 3 for summary
  const [showDeliveryTooltip, setShowDeliveryTooltip] = useState(false);
  const [showPickupTooltip, setShowPickupTooltip] = useState(false);

  useEffect(() => {
    // mounts
    dispatch(clearError());

    // component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const handleDateRangeChange = (range) => {
    //If user clicks the selected start date, it deselects it
    if (!range || !range.from) {
      setFormData({
        ...formData,
        startDate: '',
        endDate: '',
      });
      return;
    }

    setFormData({
      ...formData,
      startDate: range.from,
      endDate: range.to || '',
    });
  };

  const handleCloseModal = () => {
    dispatch(clearError());
    setShowCreateReservationModal(false);
  };

  const needsTransportConfirmation = () => {
    return formData.deliveryAssistance === 'no' || formData.pickupAssistance === 'no';
  };

  const handleNextPage = () => {
    if (currentPage === 1 && formData.startDate && formData.endDate) {
      setCurrentPage(2);
    } else if (currentPage === 2) {
      setCurrentPage(3);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage === 2) {
      setCurrentPage(1);
    } else if (currentPage === 3) {
      setCurrentPage(2);
    }
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (currentPage === 2) {
      // Move to summary page
      setCurrentPage(3);
    } else if (currentPage === 3) {
      // Actually submit the reservation
      if (needsTransportConfirmation() && !showTransportConfirmation) {
        setShowTransportConfirmation(true);
      } else {
        handleCreateReservation();
      }
    }
  };

  const handleTransportConfirmationClose = () => {
    setShowTransportConfirmation(false);
    setTransportConfirmationChecked(false);
  };

  const handleCreateReservation = async () => {
    dispatch(
      makeReservation({
        userId: userId,
        equipmentTypeId: equipment._id,
        qty: quantity,
        rentalPrice: equipment.rentalPrice * quantity,
        startDate: formData.startDate,
        endDate: formData.endDate,
        deliveryAssistance: formData.deliveryAssistance,
        pickupAssistance: formData.pickupAssistance,
        paymentMethod: formData.paymentMethod,
        additionalNotes: formData.additionalNotes,
        pickupLocation: formData.pickupLocation,
        dropOffLocation: formData.dropOffLocation,
      })
    ).then((result) => {
      if (!result.error) {
        setShowCreateReservationModal(false);
      }
    });
  };

  const handleConfirmationCheckboxChange = (e) => {
    setTransportConfirmationChecked(e.target.checked);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      {(loading || myselfError) && (
        <LoadingModal
          message={t('reservations.createModal.creatingReservation')}
          error={myselfError}
          onClose={handleDismissError}
        />
      )}

      {/* Transport Confirmation Popup */}
      {showTransportConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg border-4 border-red-500 shadow-xl w-11/12 max-w-md p-6 outline outline-2 outline-gray-200">
            <h3 className="text-xl font-semibold mb-4 text-red-600">
              {t('reservations.createModal.transportConfirmation.title')}
            </h3>
            <p className="mb-4 text-gray-700">
              {t('reservations.createModal.transportConfirmation.message')}
            </p>

            {equipment.selfTransport && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <h4 className="font-semibold text-gray-800 mb-1">
                  {t('reservations.createModal.transportConfirmation.transportRequirements')}
                </h4>
                <p className="text-gray-700">{equipment.selfTransport}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={transportConfirmationChecked}
                  onChange={handleConfirmationCheckboxChange}
                  className="form-checkbox h-5 w-5 text-green-500"
                />
                <span className="text-gray-800">
                  {t('reservations.createModal.transportConfirmation.acceptResponsibility')}
                </span>
              </label>
            </div>

            <div className="flex justify-end">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                onClick={handleTransportConfirmationClose}
                type="button"
              >
                {t('reservations.cancel')}
              </button>
              <button
                className={`bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-5 ml-4 rounded-md transition duration-200 ${
                  !transportConfirmationChecked ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleCreateReservation}
                type="button"
                disabled={!transportConfirmationChecked}
              >
                {t('reservations.createModal.transportConfirmation.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Reservation Modal */}
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-md p-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">
          {currentPage === 1 && t('reservations.createModal.title.selectDates')}
          {currentPage === 2 && t('reservations.createModal.title.inputDetails')}
          {currentPage === 3 && t('reservations.createModal.title.summary')}
        </h2>

        {/* Page 1: Date Selection */}
        {currentPage === 1 && (
          <div>
            {/* Equipment Information */}
            <div className="space-y-2 mb-6">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">
                  {t('reservations.createModal.renterName')}:
                </span>
                <span className="text-gray-900">{renterName}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">
                  {t('reservations.createModal.equipment')}:
                </span>
                <span className="text-gray-900">{equipmentName}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">
                  {t('reservations.createModal.location')}:
                </span>
                <span className="text-gray-900">{equipment.location}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">
                  {t('reservations.createModal.price')}:
                </span>
                <span className="text-gray-900">${equipment.rentalPrice * quantity}/day</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">
                  {t('reservations.createModal.quantitySelected')}:
                </span>
                <span className="text-gray-900">{quantity}</span>
              </div>
            </div>
            <div className="flex justify-center mb-6">
              {equipment && equipment._id && (
                <CalendarDialog
                  value={{
                    from: formData.startDate || null,
                    to: formData.endDate || null,
                  }}
                  onChange={handleDateRangeChange}
                  equipmentId={equipment._id}
                  quantity={quantity}
                  totalInstances={equipment.totalInstances || 0}
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">{t('reservations.startDate')}:</span>
                <span className="text-gray-900 font-bold">
                  {formData.startDate ? new Date(formData.startDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">{t('reservations.endDate')}:</span>
                <span className="text-gray-900 font-bold">
                  {formData.endDate ? new Date(formData.endDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
            <div className="flex justify-between">
              <button
                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                onClick={handleCloseModal}
                type="button"
              >
                {t('reservations.cancel')}
              </button>
              <button
                className={`bg-green-500 text-white font-semibold py-2 px-5 rounded-md transition duration-200 ${
                  !formData.startDate || !formData.endDate
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-green-600'
                }`}
                onClick={handleNextPage}
                disabled={!formData.startDate || !formData.endDate}
                type="button"
              >
                {t('reservations.createModal.next')}
              </button>
            </div>
          </div>
        )}

        {/* Page 2: Reservation Details */}
        {currentPage === 2 && (
          <form onSubmit={handleSubmitClick}>
            <div className="flex justify-between items-center mb-4">
              <label className="font-medium text-gray-700 flex items-center">
                {t('reservations.createModal.deliveryAssistance')}
                <div className="relative ml-1">
                  <span
                    onClick={() => setShowDeliveryTooltip(!showDeliveryTooltip)}
                    className="text-gray-500 cursor-pointer inline-flex items-center justify-center rounded-full bg-gray-100 w-4 h-4 text-xs hover:bg-gray-200"
                  >
                    ?
                  </span>
                  {showDeliveryTooltip && (
                    <div className="absolute bottom-6 left-0 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50">
                      {t('reservations.createModal.deliveryTooltip')}
                      <div className="absolute top-full left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                  )}
                </div>
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="deliveryAssistance"
                    value="yes"
                    checked={formData.deliveryAssistance === 'yes'}
                    onChange={handleInputChange}
                    className="form-radio"
                    required
                  />
                  <span className="text-gray-900">{t('reservations.createModal.yes')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="deliveryAssistance"
                    value="no"
                    checked={formData.deliveryAssistance === 'no'}
                    onChange={handleInputChange}
                    className="form-radio"
                    required
                  />
                  <span className="text-gray-900">{t('reservations.createModal.no')}</span>
                </label>
              </div>
            </div>
            <div className="flex justify-between items-center mb-4">
              <label className="font-medium text-gray-700">
                {t('reservations.createModal.pickupAssistance')}
                <div className="relative ml-1 inline-block">
                  <span
                    onClick={() => setShowPickupTooltip(!showPickupTooltip)}
                    className="text-gray-500 cursor-pointer inline-flex items-center justify-center rounded-full bg-gray-100 w-4 h-4 text-xs hover:bg-gray-200"
                  >
                    ?
                  </span>
                  {showPickupTooltip && (
                    <div className="absolute bottom-6 left-0 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50">
                      {t('reservations.createModal.pickupTooltip')}
                      <div className="absolute top-full left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                  )}
                </div>
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="pickupAssistance"
                    value="yes"
                    checked={formData.pickupAssistance === 'yes'}
                    onChange={handleInputChange}
                    className="form-radio"
                    required
                  />
                  <span className="text-gray-900">{t('reservations.createModal.yes')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="pickupAssistance"
                    value="no"
                    checked={formData.pickupAssistance === 'no'}
                    onChange={handleInputChange}
                    className="form-radio"
                    required
                  />
                  <span className="text-gray-900">{t('reservations.createModal.no')}</span>
                </label>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mb-4">
              <label className="block font-medium text-gray-700 mb-2">
                {t('reservations.createModal.paymentMethod')}
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">{t('reservations.createModal.selectPaymentMethod')}</option>
                <option value="credit_card">{t('reservations.createModal.creditCard')}</option>
                <option value="debit_card">{t('reservations.createModal.debitCard')}</option>
                <option value="bank_transfer">{t('reservations.createModal.bankTransfer')}</option>
                <option value="cash">{t('reservations.createModal.cash')}</option>
                <option value="check">{t('reservations.createModal.check')}</option>
              </select>
            </div>

            {/* Pickup Location */}
            <div className="mb-4">
              <label className="block font-medium text-gray-700 mb-2">
                {t('reservations.createModal.pickupLocation')}
              </label>
              <input
                type="text"
                name="pickupLocation"
                value={formData.pickupLocation}
                onChange={handleInputChange}
                placeholder={t('reservations.createModal.pickupLocationPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Drop-off Location */}
            <div className="mb-4">
              <label className="block font-medium text-gray-700 mb-2">
                {t('reservations.createModal.dropOffLocation')}
              </label>
              <input
                type="text"
                name="dropOffLocation"
                value={formData.dropOffLocation}
                onChange={handleInputChange}
                placeholder={t('reservations.createModal.dropOffLocationPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Additional Notes */}
            <div className="mb-6">
              <label className="block font-medium text-gray-700 mb-2">
                {t('reservations.createModal.additionalNotes')}
              </label>
              <textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                placeholder={t('reservations.createModal.additionalNotesPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-between">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                onClick={handlePreviousPage}
                type="button"
              >
                {t('reservations.createModal.back')}
              </button>
              <button
                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                type="submit"
              >
                {t('reservations.createModal.next')}
              </button>
            </div>
          </form>
        )}

        {/* Page 3: Selection Summary */}
        {currentPage === 3 && (
          <div>
            {/* Equipment Information */}
            <h4 className="font-semibold text-gray-800 mb-2 mt-4">
              {t('reservations.createModal.equipmentDetails')}
            </h4>

            <div className="flex justify-between">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.equipment')}:
              </span>
              <span className="text-gray-900">{equipmentName}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.location')}:
              </span>
              <span className="text-gray-900">{equipment.location}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.price')}:
              </span>
              <span className="text-gray-900">${equipment.rentalPrice * quantity}/day</span>
            </div>

            {/* Reservation Details */}
            <h4 className="font-semibold text-gray-800 mb-2 mt-4">
              {t('reservations.createModal.reservationDetails')}
            </h4>
            <div className="flex justify-between mb-1">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.renterName')}:
              </span>
              <span className="text-gray-900">{renterName}</span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="font-small text-gray-700">{t('reservations.startDate')}:</span>
              <span className="text-gray-900 font-bold">
                {formData.startDate ? new Date(formData.startDate).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="font-small text-gray-700">{t('reservations.endDate')}:</span>
              <span className="text-gray-900 font-bold">
                {formData.endDate ? new Date(formData.endDate).toLocaleDateString() : 'N/A'}
              </span>
            </div>

            <div className="flex justify-between mb-0">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.deliveryAssistance')}:
              </span>
              <span className="text-gray-900 capitalize">
                {formData.deliveryAssistance || t('reservations.createModal.notSpecified')}
              </span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.pickupAssistance')}:
              </span>
              <span className="text-gray-900 capitalize">
                {formData.pickupAssistance || t('reservations.createModal.notSpecified')}
              </span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.paymentMethod')}:
              </span>
              <span className="text-gray-900 capitalize">
                {formData.paymentMethod || t('reservations.createModal.notSpecified')}
              </span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.pickupLocation')}:
              </span>
              <span className="text-gray-900">
                {formData.pickupLocation || t('reservations.createModal.notSpecified')}
              </span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="font-small text-gray-700">
                {t('reservations.createModal.dropOffLocation')}:
              </span>
              <span className="text-gray-900">
                {formData.dropOffLocation || t('reservations.createModal.notSpecified')}
              </span>
            </div>
            {formData.additionalNotes && (
              <div className="mb-4">
                <span className="font-small text-gray-700">
                  {t('reservations.createModal.additionalNotes')}:
                </span>
                <p className="text-gray-900 mt-1">{formData.additionalNotes}</p>
              </div>
            )}

            <div className="flex justify-between mt-6">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                onClick={handlePreviousPage}
                type="button"
              >
                {t('reservations.createModal.back')}
              </button>
              <button
                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                onClick={handleSubmitClick}
                type="button"
              >
                {t('reservations.createModal.makeReservation')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { CreateReservationModal };
